import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/toaster-close-icon.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import campaignApi from '../../../utils/api/campaign';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

interface AccountDetails {
  accountCustomId: string;
  accountName: string;
}

interface TemplateDetails {
  subject: string;
  message: string;
}

type Option = {
  value: string;
  label: string;
};

/**
 * Create Campaign component handles the creation of campaign in the campaign management system.
 * @returns {any} A form interface for creating campaigns.
 */
function CreateCampaign() {
  const { t } = useTranslation('campaign');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });

  const [accountOptions, setAccountOptions] = useState<Option[]>([]);
  const [accounts, setAccounts] = useState<AccountDetails[]>([]);
  const [templates, setTemplates] = useState<TemplateDetails[]>([]);
  const [templateOptions, setTemplateOptions] = useState<Option[]>([]);
  const [formData, setFormData] = useState<{
    template: string;
    communicationMedium: string;
    accounts: string[];
  }>({
    template: '',
    communicationMedium: 'sms',
    accounts: [],
  });
  const [filter, setFilter] = useState<{
    communicationMedium: string;
  }>({
    communicationMedium: 'sms',
  });
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  /**
   * Fetch accounts function
   */
  const fetchAccounts = () => {
    campaignApi
      .fetchAccounts(filter)
      .then((response) => {
        const options = response.data.map(
          (account: { accountCustomId: string; accountName: string }) => ({
            value: account.accountCustomId,
            label: account.accountName || account.accountCustomId,
          }),
        );

        setAccountOptions(options);
        setAccounts(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Unable to get accounts!',
            },
          }),
        );
      });
  };

  /**
   * Function to reset configuration input
   */
  const handleResetInput = () => {
    setModalBoxStatus('close');
    setFormData({
      template: '',
      communicationMedium: 'sms',
      accounts: [],
    });
  };

  /**
   * Fetch accounts function
   */
  const fetchTemplates = () => {
    campaignApi
      .fetchTemplates(filter)
      .then((response) => {
        const options = response.data.map((template: TemplateDetails) => ({
          value: template.subject,
          label: template.subject,
        }));

        setTemplateOptions(options);
        setTemplates(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Unable to get templates!',
            },
          }),
        );
      });
  };

  /**
   * Run campaign
   */
  const runCampaign = () => {
    if (!formData.template || !formData.accounts.length || !formData.communicationMedium) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
      return;
    }

    setFormStatus({
      showFormErrors: false,
      buttonLoader: true,
    });

    const request: any = {
      accountCustomIds: formData.accounts,
      communicationMedium: formData.communicationMedium,
    };

    campaignApi
      .runCampaign(request)
      .then(() => {
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('greatCampaignOfficially')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleResetInput}
            />
          ),
          notificationModal: true,
        });
        setModalBoxStatus('open');
        setFormStatus({
          showFormErrors: false,
          buttonLoader: false,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: 0,
          }),
        );
        setFormStatus({
          showFormErrors: false,
          buttonLoader: false,
        });
      });
  };

  useEffect(() => {
    fetchAccounts();
    fetchTemplates();
  }, [filter]);

  /**
   * Input field change function
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleCommunicationMediumChange = (name: string, changeValue: string | number) => {
    setFormData({
      template: '',
      communicationMedium: 'sms',
      accounts: [],
    });
    setFilter({
      ...filter,
      [name]: changeValue,
    });
  };

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Resets the form state and navigates back to the device management page.
   * This is called after successful submission or when user confirms cancellation.
   */
  const handleNavigateBack = () => {
    setFormData({
      template: '',
      communicationMedium: 'sms',
      accounts: [],
    });

    navigate('../');
  };

  /**
   * Displays a warning modal when user attempts to cancel form submission.
   * Gives users the option to confirm cancellation or continue editing.
   */
  const handleClickCancel = () => {
    handleNavigateBack();
  };

  /**
   * Remove recipient
   * @param {any} accountCustomId - The accountCustomId
   */
  const removeRecipient = (accountCustomId: string) => {
    setFormData((previousState) => ({
      ...previousState,
      accounts: previousState.accounts.filter((account) => account !== accountCustomId),
    }));
  };

  return (
    <div className="create-campaign-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />

      <div className="create-campaign-header">
        <PageTitle title={t('createCampaign')} />
      </div>
      <div className="create-campaign-container">
        <div className="create-campaign-form-wrap">
          <div className="create-campaign-form">
            <RadioButton
              name="communicationMedium"
              value={filter.communicationMedium}
              list={[
                {
                  label: t('SMS'),
                  value: 'sms',
                },
                {
                  label: t('EMAIL'),
                  value: 'email',
                },
              ]}
              onChangeValue={handleCommunicationMediumChange}
            />
          </div>
          <div className="create-campaign-form">
            <MultiSelectDropdown
              placeholderFilled={t('account(s)')}
              title={t('recipients')}
              options={accountOptions}
              name="accounts"
              placeholder={t('selectAccounts')}
              search
              valueRequired
              selectedValues={formData.accounts}
              onSelectionChange={handleInputChange}
            />
            <SelectField
              inputLabel={t('template')}
              name="template"
              placeholder={t('selectTemplate')}
              options={templateOptions}
              selectedValue={formData.template}
              valueRequired
              onChangeValue={handleInputChange}
              error={formStatus.showFormErrors && !formData.template}
              errorMessage={t('templateRequired')}
            />
          </div>
          <div className="create-campaign-form-no-flex">
            <div className="create-campaign-form-title">
              <span className="title">{t('selectedRecipients')}:</span>
            </div>
            {formData.accounts.length ? (
              <div className="tag-row">
                {formData.accounts.map((account) => {
                  const accountDetail: AccountDetails = accounts.find(
                    (accumulator) => account === accumulator.accountCustomId,
                  )!;

                  const filterTag = (
                    <div className="tag-wrap" key={accountDetail?.accountCustomId}>
                      <div className="tag-details">
                        <h6>{accountDetail?.accountName}: </h6>
                        <p>{accountDetail?.accountCustomId}</p>
                      </div>
                      <span
                        className="close-icon"
                        role="presentation"
                        onClick={() => removeRecipient(accountDetail?.accountCustomId)}>
                        <CloseIcon />
                      </span>
                    </div>
                  );

                  return filterTag;
                })}
              </div>
            ) : (
              <p>{t('noRecipientSelected')}</p>
            )}
          </div>
          <div className="create-campaign-form-no-flex">
            <div className="create-campaign-form-title">
              <span className="title">
                {t('message')} ({t('template')}):
              </span>
            </div>
            <p>
              {templates.find((template) => template.subject === formData.template)?.message ??
                t('notemplateSelected')}
            </p>
          </div>
        </div>
        <div className="create-campaign-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label={t('runCampaign')}
              buttonType="submit"
              onClickFunc={runCampaign}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateCampaign;
